import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import 'dayjs/locale/ar';
import 'dayjs/locale/bn';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/ht';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/ur';
import 'dayjs/locale/zh';
import { Address, BookingMode, Event, Provider, Slot, Window } from '../../types/Site';

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(timezone);

const METERS_PER_MILE = 1609.34;

// Currently unused by any neighbor-facing surface: no Sites operation vends a neighbor-relative
// distance (GetSitesWithinRadius measures from the map's search center, and SearchAllSites/
// DescribeSite carry none), so every distance a neighbor sees is computed client-side in miles
// from their own resolved location via utils/distance -- see useSite.ts's distanceMiles. Kept for
// the moment against DescribeSite growing an origin param, at which point a server-provided
// meters figure becomes renderable again.
export function metersToMiles(meters: number): string {
  return (meters / METERS_PER_MILE).toFixed(1);
}

export function bookingModeLabel(bookingMode: BookingMode): string {
  switch (bookingMode) {
    case 'walkIn':
      return 'Walk-in';
    case 'reservation':
      return 'Reservation';
    case 'preRegistration':
      return 'Pre-registration';
    default:
      return 'Unknown';
  }
}

// Site.timezone is still-stabilizing API data with no format guarantee -- dayjs's .tz() throws a
// RangeError on a malformed IANA zone name, which would otherwise crash the render of whichever
// surface (site card, details screen) is formatting the time.
export function isValidTimeZone(timeZone: string): boolean {
  try {
    // eslint-disable-next-line no-new -- constructing is the only way to validate the zone name
    new Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

// `timeZone` is the pantry's own IANA zone (Site.timezone) -- falls back to the device zone when
// it's missing or malformed, rather than throwing on an invalid zone name.
function toZoned(input: string | Date, timeZone?: string): dayjs.Dayjs {
  const d = dayjs(input);
  return timeZone && isValidTimeZone(timeZone) ? d.tz(timeZone) : d;
}

export function formatEventWindow(window: Window, timeZone?: string): string {
  const opensAt = toZoned(window.opensAt, timeZone);
  const closesAt = toZoned(window.closesAt, timeZone);
  return `${opensAt.format('ddd, MMM D')} ${opensAt.format('h:mm A')} - ${closesAt.format('h:mm A')}`;
}

export type BookingBadgeVariant = 'walkIn' | 'reservation' | 'preRegistration' | 'unknown';

// Every BookingMode gets a badge -- 'unknown' gets a neutral/gray one rather than none, so a
// neighbor never mistakes an unmodeled booking mode for a plain walk-in (see EventBlock).
export function bookingBadgeVariant(bookingMode: BookingMode): BookingBadgeVariant {
  if (bookingMode === 'walkIn') return 'walkIn';
  if (bookingMode === 'preRegistration') return 'preRegistration';
  if (bookingMode === 'reservation') return 'reservation';
  return 'unknown';
}

// Takes the already-resolved variant, not the raw BookingMode -- callers only reach for a label
// once bookingBadgeVariant has told them which badge to show.
export function bookingModeTranslationKey(variant: BookingBadgeVariant): string {
  if (variant === 'walkIn') return 'site_list_booking_walkin';
  if (variant === 'preRegistration') return 'site_list_booking_preregistration';
  if (variant === 'reservation') return 'site_list_booking_reservation';
  return 'site_list_booking_unknown';
}

export function formatClockTime(iso: string, timeZone?: string, locale: string = 'en'): string {
  return toZoned(iso, timeZone).locale(locale).format('h:mm A');
}

// Single trailing AM/PM (matching the closing time's meridiem) rather than one per side when both
// endpoints share a meridiem -- matches the design's own "10–1 PM" treatment for a 10am-1pm
// window. Falls back to a meridiem on each side when the window crosses noon/midnight, since a
// single trailing meridiem is only correct when both ends agree on it (otherwise a 22:00-01:00
// window would render as "10-1 AM", twelve hours off on the open side).
export function formatChipTimeRange(window: Window, timeZone?: string, locale: string = 'en'): string {
  const opensAt = toZoned(window.opensAt, timeZone).locale(locale);
  const closesAt = toZoned(window.closesAt, timeZone).locale(locale);
  const formatHour = (d: dayjs.Dayjs) => (d.minute() === 0 ? d.format('h') : d.format('h:mm'));
  if (opensAt.format('A') === closesAt.format('A')) {
    return `${formatHour(opensAt)}–${formatHour(closesAt)} ${closesAt.format('A')}`;
  }
  return `${formatHour(opensAt)} ${opensAt.format('A')}–${formatHour(closesAt)} ${closesAt.format('A')}`;
}

// "Today"/"Tomorrow" over a weekday-only label when applicable, per the design's low-literacy
// guidance -- callers supply already-translated labels rather than this formatter owning i18n.
export function formatChipDayLabel(
  iso: string,
  now: Date,
  todayLabel: string,
  tomorrowLabel: string,
  timeZone?: string,
  locale: string = 'en'
): string {
  const date = toZoned(iso, timeZone);
  // Compare calendar-day strings (in the pantry's own zone) rather than dividing a raw millisecond
  // diff by 24h -- that diff truncates incorrectly on a 23h/25h local day around a DST transition,
  // which could mislabel a window opening tomorrow as "Today".
  const todayStr = toZoned(now, timeZone).format('YYYY-MM-DD');
  const tomorrowStr = toZoned(now, timeZone).add(1, 'day').format('YYYY-MM-DD');
  const dateStr = date.format('YYYY-MM-DD');
  if (dateStr === todayStr) return todayLabel;
  if (dateStr === tomorrowStr) return tomorrowLabel;
  return date.locale(locale).format('ddd M/D');
}

export function formatAddressSummary(site: { address?: Address; provider?: Provider }): string {
  if (site.address?.street1) {
    return site.address.city ? `${site.address.street1}, ${site.address.city}` : site.address.street1;
  }
  // Never surface the raw SiteSource enum value (e.g. "plentifulProvider") to a neighbor --
  // fall back to the provider's human-readable name, or an empty string rather than any
  // developer-facing placeholder.
  return site.provider?.name ?? '';
}

// The device's own IANA zone, for deciding whether a "times shown in the pantry's zone" note is
// worth showing at all -- there's no point calling it out when the two zones already match.
export function deviceTimeZone(): string {
  return dayjs.tz.guess();
}

// Short zone abbreviation (e.g. "EDT") for the timezone note -- accounts for DST at the given
// instant rather than a fixed offset, since a zone's abbreviation (and offset) can differ between
// a window's own time and `now`. Empty string for a malformed zone -- callers should already be
// gating the note on isValidTimeZone, but this stays crash-safe on its own.
export function timeZoneAbbreviation(timeZone: string, at: Date): string {
  return isValidTimeZone(timeZone) ? dayjs(at).tz(timeZone).format('z') : '';
}

export function formatSlotTime(slot: Slot, timeZone?: string): string {
  const opensAt = toZoned(slot.opensAt, timeZone);
  const closesAt = toZoned(slot.closesAt, timeZone);
  if (!opensAt.isValid() || !closesAt.isValid()) return '--';
  return `${opensAt.format('h:mm A')} - ${closesAt.format('h:mm A')}`;
}

export function formatRetrievedAt(retrievedAt: string, timeZone?: string): string {
  const at = toZoned(retrievedAt, timeZone);
  return at.isValid() ? at.format('h:mm A') : '--';
}

export function eventDisplayName(event: Event, fallback: string): string {
  return event.name || fallback;
}

// A malformed/missing opensAt (still-stabilizing API data) parses to NaN -- sort with a comparator
// that can return NaN is unspecified, so treat invalid dates as sorting last rather than leaving
// their order undefined. Shared by SiteDetailsView's window tiles and EventSlotPickerModal's slots.
export function compareByOpensAt(a: { opensAt: string }, b: { opensAt: string }): number {
  const aTime = new Date(a.opensAt).getTime();
  const bTime = new Date(b.opensAt).getTime();
  if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
  if (Number.isNaN(aTime)) return 1;
  if (Number.isNaN(bTime)) return -1;
  return aTime - bTime;
}
